/* eslint-disable react/no-array-index-key */
import React, { FC, useEffect, useState } from 'react'
import { Box, Flex, Image, SimpleCarousel } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { spacing, theme } from '@moonpig/launchpad-theme'
import { FavouriteButton } from '../FavouriteButton'
import { useLocaleText } from './ImageCarouselVariant.locale'
import { CardScenePreview } from '../CardScenePreview'
import { DispatchCutOff } from '../../types'
import { DispatchBanner } from '../../modules/Dispatch'

const SCENE_INDEX = 0
const COVER_IMAGE_INDEX = 1

const StyledImage = styled(Image)<{ p?: number }>`
  object-fit: contain;
  ${({ p }) => s({ p })}
`

const StyledCarousel = styled(SimpleCarousel)`
  padding: 0;
  & > div > ul {
    gap: 16px;
  }
`

const StyledPreviewContainer = styled(Box)`
  ${s({
    borderRadius: '16px 16px 0 0',
    overflow: 'hidden',
    bgcolor: 'colorBackground02',
    width: 1,
  })}
`
const StyledDispatchBannerWrapper = styled(Box)`
  ${s({
    borderRadius: '0 0 16px 16px',
    overflow: 'hidden',
  })}
`

const StyledFavButton = styled(FavouriteButton)`
  ${s({
    position: 'absolute',
    top: 8,
    right: 8,
  })}
`

const SceneItem: FC<{
  url: string
  selected?: boolean
}> = ({ url, ...props }) => {
  return (
    <StyledPreviewContainer {...props}>
      <CardScenePreview coverImageUrl={url} />
    </StyledPreviewContainer>
  )
}

const PreviewItem: FC<{
  title: string
  url: string
  zoomUrl?: string
  p?: number
  selected?: boolean
  onClick?: () => void
}> = ({ title, url, zoomUrl, p, selected, onClick, ...props }) => {
  return (
    <StyledPreviewContainer
      onClick={onClick}
      as={'div'}
      aria-selected={selected}
      {...props}
    >
      <StyledImage
        aspectRatio={1}
        src={url}
        zoomSrc={zoomUrl}
        title={title}
        p={p}
        loading={'eager'}
      />
    </StyledPreviewContainer>
  )
}

const StyledThumbnailContainer = styled(Box)`
  ${s({
    borderRadius: 4,
    overflow: 'hidden',
    bgcolor: 'colorBackground02',
    width: 1,
    borderColor: 'transparent',
    borderBottom: 3,
    borderTop: 3,
    borderLeft: 3,
    borderRight: 3,
  })}

  &:is(button)[aria-selected='true'] {
    ${s({
      borderColor: 'colorInteractionSelectedState',
    })}
  }

  &:is(button):hover {
    ${s({
      borderColor: 'colorInteractionButtonHover',
    })}
  }
`

const ThumbnailItem: FC<{
  title: string
  url: string
  zoomUrl?: string
  p?: number
  selected?: boolean
  onClick?: () => void
}> = ({ title, url, zoomUrl, p, selected, onClick, ...props }) => {
  return (
    <StyledThumbnailContainer
      onClick={onClick}
      as={'button'}
      aria-selected={selected}
      {...props}
    >
      <StyledImage
        aspectRatio={1}
        src={url}
        zoomSrc={zoomUrl}
        title={title}
        p={p}
        loading={selected ? 'eager' : 'lazy'}
      />
    </StyledThumbnailContainer>
  )
}

type Props = {
  productId: string
  title: string
  images: Array<{ url: string }>
  initialSelectedIndex: number
  isFavourited: boolean
  onFavouriteToggle: () => void
  onImageSelected: (event: { index: number }) => void
  dispatchCutOff: DispatchCutOff | null
}

export const ImageCarouselVariantFrictionlessFlow: FC<Props> = ({
  productId,
  title,
  images,
  initialSelectedIndex,
  isFavourited,
  onFavouriteToggle,
  onImageSelected,
  dispatchCutOff,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const t = useLocaleText()

  useEffect(() => {
    setSelectedIndex(initialSelectedIndex)
  }, [initialSelectedIndex, productId])

  return (
    <Flex
      mb={6}
      gap={spacing(6)({ theme })}
      flexDirection={'column'}
      maxWidth="30.25rem"
      margin="0 auto"
    >
      <Box
        position={'relative'}
        data-product-id={productId}
        className="product-image-carousel"
      >
        <Box display={selectedIndex === SCENE_INDEX ? 'block' : 'none'}>
          <SceneItem url={images[COVER_IMAGE_INDEX].url} />
        </Box>
        <Box display={selectedIndex !== SCENE_INDEX ? 'block' : 'none'}>
          <PreviewItem
            title={title}
            url={images[selectedIndex].url}
            zoomUrl={images[selectedIndex].url}
            p={8}
          />
        </Box>
        <StyledFavButton
          title={title}
          isFavourited={isFavourited}
          onFavouriteToggle={onFavouriteToggle}
          isCard
        />
        {dispatchCutOff && (
          <StyledDispatchBannerWrapper>
            <DispatchBanner dispatchCutOff={dispatchCutOff} />
          </StyledDispatchBannerWrapper>
        )}
      </Box>
      <StyledCarousel aria-label={t('image_carousel_aria_label')}>
        {images.map((image, index) => (
          <Box key={index} width={{ xs: '5rem', md: '6rem', lg: '7.35rem' }}>
            <ThumbnailItem
              title={title}
              url={image.url}
              p={4}
              selected={selectedIndex === index}
              onClick={() => {
                setSelectedIndex(index)
                onImageSelected({ index })
              }}
            />
          </Box>
        ))}
      </StyledCarousel>
    </Flex>
  )
}
